import React, { Component } from 'react';
import PropTypes from 'prop-types';
import { withTranslation } from 'react-i18next';
import FormsElementContainer from '../FormsElementContainer/FormsElementContainer';

// Enumerables
import { Tooltip, TooltipPlacement } from '../../../../atoms/Tooltip';
import { FormLabelModifier } from '../FormLabel/FormLabel';

// Utilities
import bem from '../../../../../lib/bemUtils/bem';
import { createDescribedById } from '../FormLabel/utilities';
import { getCharCount } from '../../lib/inputHelper';

// Components
import FormLabel from '../FormLabel/FormLabel';
import { Icons, SVG } from '../../../../atoms/SVG';

// Shapes
import EnumerableShape from '../../../../../lib/shapes/EnumerableShape';

import { Namespace } from '../../../../../translation/namespace';
import FormFieldWrapper from '../FormFieldWrapper/FormFieldWrapper';
import withAppContext from '../../../../hocs/withAppContext/withAppContext';

import { InputType } from '../../InputTypes';

class FormInput extends Component {
  constructor(props) {
    super(props);

    this.input = React.createRef();
    this.resetButtonRef = React.createRef();
    this.state = {
      value: props.value,
      showResetButton: false,
      charCount: 0,
    };

    // Bindings
    this.onChange = this.onChange.bind(this);
    this.onInput = this.onInput.bind(this);
    this.onInvalid = this.onInvalid.bind(this);
    this.validate = this.validate.bind(this);
    this.unsetCustomValidity = this.unsetCustomValidity.bind(this);
    this.setCustomValidity = this.setCustomValidity.bind(this);
  }

  componentDidMount() {
    // Populate it's validation function to the submit event
    this.setCustomValidity(this.props.validationMessage);

    // Validate if the text value provided by BE is too long (mostly due to old data)
    if (this.props.value && this.props.maxLength && this.props.value.length > this.props.maxLength) {
      this.setCustomValidity(this.props.t(`${Namespace.Form}:tooManyCharacters`));
    }

    if (this.props.formInputRef) {
      this.props.formInputRef.current = this.input.current;
    }
  }

  componentDidUpdate(prevProps) {
    // Setting validation message from outside, for e.g input fiels or other things
    // for date input fields we dont use the changehandler
    if (prevProps.validationMessage !== this.props.validationMessage) {
      this.setCustomValidity(this.props.validationMessage);
    }

    // for setting the state from outside, like fromscombobox
    // (on selection in combobox prevProps.value and props.value are never null or undefined, see: SON-12478)
    if (prevProps.value && this.props.value && this.props.value !== prevProps.value) {
      // OK to setState here within an if clause
      this.setState({ value: this.props.value });
    }

    // resetbutton
    if (!this.props.value && prevProps.value) {
      // OK to setState here within an if clause
      this.setState({ value: null });
    }
  }

  setCustomValidity(message = '') {
    if (this.input.current && this.input.current.setCustomValidity) {
      if (message) {
        this.input.current.setCustomValidity(message);
        return true;
      }
      this.input.current.setCustomValidity('');
      return false;
    }
    return false;
  }

  // This function is a wrapper for setCustomValidity (improve semantic)
  unsetCustomValidity() {
    this.setCustomValidity();
  }

  // This function will be populated to the Form
  validate() {
    if (this.props.validationMessage) {
      if (this.props.validationMessage.length > 0) {
        this.setCustomValidity(this.props.validationMessage);
        return false;
      }

      this.unsetCustomValidity();
      return true;
    }

    return true;
  }

  showResetButton(event) {
    if (event.target.value.length > 0) {
      this.setState({ showResetButton: true });
    }
  }

  changeHandler(event, changeCallback) {
    this.unsetCustomValidity();
    this.validate();

    const { value } = event.target;

    this.setState({ value });

    if (typeof changeCallback === 'function') {
      changeCallback(event);
    }

    // fields can not! only contain white spaces.
    // check for >= 1 for normal validation of fields
    if (this.props.type === InputType.TEXT && value.length >= 1 && value?.trim?.() === '') {
      this.setCustomValidity(this.props.t(`${Namespace.Form}:fieldCantBeEmpty`));
    }

    // feed the char counter with data
    this.setState({ charCount: getCharCount(event) });

    this.showResetButton(event);
  }

  onInvalid(event) {
    if (event.target.validity && event.target.validity.patternMismatch) {
      this.setCustomValidity(this.props.patternMismatchErrorMessage);
    }
  }

  onChange(event) {
    this.changeHandler(event, this.props.onChange);
  }

  onInput(event) {
    this.changeHandler(event, this.props.onInput);
  }

  getResetClass() {
    const cssClass = ['form-input__reset'];
    if (this.state.showResetButton) {
      cssClass.push('form-input__reset--show');
    }
    return cssClass.join(' ');
  }

  getAriaDescribedBy() {
    if (this.props.ariaDescribedBy) {
      return this.props.ariaDescribedBy;
    }

    if (this.props.additionalDescription || this.props.labelHelpText) {
      return createDescribedById(this.props.id);
    }

    return null;
  }

  // some inputs dont have a reset button, so we only hide it when its active
  hideResetButton() {
    if (this.props.resetButton) {
      this.setState({ showResetButton: false });
    }
  }

  renderInput() {
    return (
      <>
        {!this.props.labelHidden && (
          /**
           * readonly will disable additional information
           * on labels like minLength, maxLength and helpText
           */
          <FormLabel
            id={this.props.labelId}
            htmlFor={this.props.id}
            helpText={
              this.props.readOnly || this.props.additionalDescription !== null ? null : this.props.labelHelpText
            }
            helpTextModifier={this.props.labelHelpTextModifier}
            tooltipPlacement={this.props.tooltipPlacement}
            modifier={this.props.modifierLabel}
            required={this.props.readOnly ? null : this.props.required || this.props.ariaRequired}
            minLength={this.props.readOnly || this.props.hideLabelAddition ? null : this.props.minLength}
            type={this.props.type}
            extendedInfoSVG={this.props.extendedInfoSVG}
          >
            {this.props.labelTitle}
          </FormLabel>
        )}
        <FormFieldWrapper
          maxLength={this.props.maxLength && !this.props.readOnly ? this.props.maxLength : null}
          currentCount={this.state.charCount}
          showAdditionalDescription={
            typeof this.props.additionalDescription === 'string' &&
            this.props.additionalDescription !== '' &&
            !this.props.readOnly
          }
          additionalDescription={this.props.additionalDescription}
          describedById={createDescribedById(this.props.id)}
        >
          {/* if used correctly and wisely, accesskeys are a11y enhancement */}
          {/* eslint-disable-next-line jsx-a11y/no-access-key */}
          <input
            id={this.props.id}
            ref={this.input}
            className={bem('form-input', this.props.modifierInput)}
            type={this.props.type}
            name={this.props.name}
            disabled={this.props.disabled}
            readOnly={this.props.readOnly}
            placeholder={this.props.placeholder}
            value={this.state.value || ''}
            autoComplete={this.props.autoComplete}
            // Validation
            required={this.props.required}
            minLength={this.props.minLength}
            maxLength={this.props.maxLength}
            min={this.props.min}
            max={this.props.max}
            step={this.props.step}
            pattern={this.props.pattern}
            // Accessibility
            role={this.props.ariaRole}
            aria-label={this.props.labelHidden ? this.props.labelTitle : null}
            aria-owns={this.props.ariaOwns}
            aria-controls={this.props.ariaControls}
            aria-activedescendant={this.props.ariaActivedescendant}
            aria-expanded={this.props.ariaExpanded}
            aria-describedby={this.getAriaDescribedBy()}
            aria-autocomplete={this.props.ariaAutoComplete}
            aria-haspopup={this.props.ariaHasPopup}
            aria-required={this.props.ariaRequired ? true : undefined}
            accessKey={this.props.userInfo.settings.accessKeysEnabled ? this.props.accessKey : null}
            // Event handlers
            onKeyDown={this.props.onKeyDown}
            onKeyUp={this.props.onKeyUp}
            onKeyPress={this.props.onKeyPress}
            onChange={this.onChange}
            onInput={this.onInput}
            onMouseDown={this.props.onMouseDown}
            onFocus={event => {
              this.setState({ charCount: getCharCount(event) });
              if (this.props.onFocus) {
                this.props.onFocus(event);
              }
              this.showResetButton(event);
            }}
            onBlur={event => {
              this.hideResetButton();
              if (this.props.onBlur) {
                this.props.onBlur(event);
              }
            }}
            onInvalid={this.onInvalid}
            onPaste={this.props.onPaste}
          />
        </FormFieldWrapper>
        {this.props.resetButton === true && this.input && this.input.current && this.input.current.value !== '' && (
          <Tooltip attachTo={this.resetButtonRef} placement="bottom" hideTooltipFromScreenReader>
            <button
              onClick={e => {
                this.setState({
                  value: null,
                  showResetButton: false,
                });
                this.input.current.value = '';
                this.input.current.focus();
                if (this.props.onInput) {
                  this.props.onInput(e);
                }
              }}
              ref={this.resetButtonRef}
              type="reset"
              className={this.getResetClass()}
              title={this.props.t('buttonReset.title')}
            >
              <span className="invisible">{this.props.t('buttonReset.name')}</span>
              <SVG inverted svgSpriteId={Icons.DECLINE_CROSS} svgClass="form-input__reset-icon" />
            </button>
          </Tooltip>
        )}
      </>
    );
  }

  render() {
    // in some cases, the input's FormElementContainer is bad for semantics
    // and harms screen reader output. see <InstantSearch>),
    // container would kill combobox semantic here.
    if (this.props.inputInContainer) {
      return <FormsElementContainer>{this.renderInput()}</FormsElementContainer>;
    }
    return <>{this.renderInput()}</>;
  }
}

FormInput.propTypes = {
  formInputRef: PropTypes.oneOfType([PropTypes.func, PropTypes.shape({ current: PropTypes.shape({}) })]),
  id: PropTypes.string.isRequired,
  labelId: PropTypes.string,
  type: PropTypes.oneOf(Object.values(InputType)),
  name: PropTypes.string,
  disabled: PropTypes.bool,
  readOnly: PropTypes.bool,
  labelHidden: PropTypes.bool,
  hideLabelAddition: PropTypes.bool,
  placeholder: PropTypes.string,
  value: PropTypes.string,
  inputInContainer: PropTypes.bool,
  labelTitle: PropTypes.string.isRequired,
  labelHelpText: PropTypes.string,
  labelHelpTextModifier: PropTypes.string,
  extendedInfoSVG: PropTypes.shape({
    helpText: PropTypes.string,
    svgSpriteID: PropTypes.string,
    tooltipPlacement: PropTypes.string,
  }),
  tooltipPlacement: EnumerableShape(TooltipPlacement),
  autoComplete: PropTypes.string,
  resetButton: PropTypes.bool,
  additionalDescription: PropTypes.string,
  // Modifier
  modifierLabel: EnumerableShape(FormLabelModifier),
  modifierInput: PropTypes.string,

  // Validations
  required: PropTypes.bool,
  pattern: PropTypes.string,
  patternMismatchErrorMessage: PropTypes.string,
  minLength: PropTypes.number,
  maxLength: PropTypes.number,
  min: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
  max: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
  step: PropTypes.number,
  validationMessage: PropTypes.string,

  // Accesibillity
  ariaRequired: PropTypes.bool,
  ariaRole: PropTypes.string,
  ariaOwns: PropTypes.string,
  ariaControls: PropTypes.string,
  ariaExpanded: PropTypes.bool,
  ariaActivedescendant: PropTypes.string,
  ariaAutoComplete: PropTypes.oneOf(['inline', 'list', 'both']),
  ariaDescribedBy: PropTypes.string,
  ariaHasPopup: PropTypes.oneOf(['true', 'listbox']),
  accessKey: PropTypes.string,
  userInfo: PropTypes.shape({
    settings: PropTypes.shape({
      accessKeysEnabled: PropTypes.bool,
    }),
  }).isRequired,

  // Event Handler
  onChange: PropTypes.func,
  onInput: PropTypes.func,
  onKeyUp: PropTypes.func,
  onKeyDown: PropTypes.func,
  onKeyPress: PropTypes.func,
  onFocus: PropTypes.func,
  onBlur: PropTypes.func,
  onPaste: PropTypes.func,
  onMouseDown: PropTypes.func,

  // Translation
  t: PropTypes.func.isRequired,
};

FormInput.defaultProps = {
  formInputRef: null,
  type: InputType.TEXT,
  name: undefined,
  disabled: false,
  readOnly: false,
  inputInContainer: true,
  labelHidden: false,
  hideLabelAddition: false,
  placeholder: null,
  value: null,
  labelId: null,
  labelHelpText: undefined,
  labelHelpTextModifier: undefined,
  extendedInfoSVG: null,
  tooltipPlacement: undefined,
  autoComplete: null,
  resetButton: false,
  additionalDescription: null,
  // Modifier
  modifierLabel: null,
  modifierInput: null,

  // Validations
  required: null,
  pattern: null,
  patternMismatchErrorMessage: null,
  minLength: null,
  maxLength: null,
  min: null,
  max: null,
  step: null,
  validationMessage: null,

  // Aria
  ariaRole: null,
  ariaOwns: null,
  ariaControls: null,
  ariaExpanded: null,
  ariaActivedescendant: null,
  ariaAutoComplete: null,
  ariaRequired: false,
  ariaDescribedBy: null,
  ariaHasPopup: null,
  accessKey: null,

  // Event Handler
  onChange: null,
  onKeyUp: null,
  onInput: null,
  onKeyDown: null,
  onKeyPress: null,
  onFocus: null,
  onBlur: null,
  onPaste: null,
  onMouseDown: null,
};

export default withTranslation(Namespace.FormInput, { withRef: true })(withAppContext(FormInput));
